<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="tela pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">
                        Previsão de Faturamento — {{ mesReferencia }}
                    </v-toolbar-title>
                    <v-spacer/>
                </v-toolbar>

                <v-row class="ma-1" dense>
                    <v-col cols="12" sm="6" md="3">
                        <v-card elevation="2">
                            <v-card-text>
                                <div class="text-caption grey--text">Previsto no mês</div>
                                <div class="text-h5 font-weight-bold primary--text">
                                    {{ formatCurrency(previstoTotal) }}
                                </div>
                                <div class="caption">
                                    {{ qtdAssinaturasAtivas }} clientes previstos ·
                                    {{ clientesAtivos }} clientes ativos
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card elevation="2">
                            <v-card-text>
                                <div class="text-caption grey--text">Já recebido</div>
                                <div class="text-h5 font-weight-bold success--text">
                                    {{ formatCurrency(recebidoTotal) }}
                                </div>
                                <div class="caption">
                                    {{ qtdRecebido }} cobranças · {{ percentRecebido }}% do previsto
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card elevation="2">
                            <v-card-text>
                                <div class="text-caption grey--text">A receber</div>
                                <div class="text-h5 font-weight-bold" style="color:#1976d2">
                                    {{ formatCurrency(aReceberTotal) }}
                                </div>
                                <div class="caption">
                                    {{ qtdAReceber }} cobranças previstas
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card elevation="2">
                            <v-card-text>
                                <div class="text-caption grey--text">Em atraso (bloqueados)</div>
                                <div class="text-h5 font-weight-bold error--text">
                                    {{ formatCurrency(atrasoTotal) }}
                                </div>
                                <div class="caption">
                                    {{ qtdAtraso }} clientes bloqueados · mensal
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row class="ma-1" dense>
                    <v-col cols="12" md="8">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="text-subtitle-1">
                                Previsão por semana
                                <v-spacer/>
                                <span class="caption grey--text">
                                    vencimento em fim de semana cai no próximo dia útil
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <BarChart
                                    v-if="dados.length"
                                    :labels="dados.map(d => d.label)"
                                    :datasets="[
                                        { label: 'Recebido', data: dados.map(d => Number(d.recebido)), color: 'rgba(76, 175, 80, 0.75)' },
                                        { label: 'A receber', data: dados.map(d => Number(d.a_receber)), color: 'rgba(25, 118, 210, 0.7)' },
                                    ]"
                                    :stacked="true"
                                    :height="alturaGrafico"
                                />
                                <div v-else class="text-center grey--text pa-6">
                                    Sem dados
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="4">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="text-subtitle-1">
                                Em atraso (bloqueados)
                                <v-spacer/>
                                <v-btn icon small @click="mostrarAtraso = !mostrarAtraso">
                                    <v-icon>{{ mostrarAtraso ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                </v-btn>
                            </v-card-title>
                            <v-card-text v-if="mostrarAtraso" class="pa-0 lista-rolante">
                                <v-list dense v-if="emAtraso.length">
                                    <v-list-item v-for="(emp, i) in emAtraso" :key="i">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ emp.fantasia || emp.razao_social }}</v-list-item-title>
                                            <v-list-item-subtitle class="caption">
                                                {{ emp.periodicidade }} · {{ formatCurrency(emp.valor) }}
                                                <span v-if="emp.vencimento"> · venc. {{ formatDate(emp.vencimento) }}</span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                                <div v-else class="text-center grey--text pa-4">
                                    Nenhum cliente bloqueado
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row class="ma-1" dense>
                    <v-col cols="12" md="5">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="text-subtitle-1">
                                Planos anuais de {{ mesReferencia }}
                                <v-spacer/>
                                <span class="font-weight-bold">{{ formatCurrency(totalAnuaisMes) }}</span>
                            </v-card-title>
                            <v-card-text class="pa-0">
                                <v-simple-table dense v-if="anuaisMes.length">
                                    <thead>
                                        <tr>
                                            <th>Empresa</th>
                                            <th>Vencimento</th>
                                            <th class="text-right">Valor</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="p in anuaisMes" :key="p.empresa_id">
                                            <td>{{ p.fantasia || p.razao_social }}</td>
                                            <td>{{ formatDate(p.vencimento) }}</td>
                                            <td class="text-right">{{ formatCurrency(p.valor) }}</td>
                                            <td>
                                                <v-chip x-small :color="p.pago ? 'success' : 'primary'" dark>
                                                    {{ p.pago ? 'Pago' : 'A receber' }}
                                                </v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                                <div v-else class="text-center grey--text pa-6">
                                    Nenhuma renovação anual neste mês
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="7">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="text-subtitle-1">
                                Planos anuais de {{ anoReferencia }}
                                <v-spacer/>
                                <span class="caption grey--text mr-2">
                                    {{ anuaisPagos }} pagos · {{ anuaisAReceber }} a receber
                                </span>
                                <span class="font-weight-bold">{{ formatCurrency(totalAnuaisAno) }}</span>
                            </v-card-title>
                            <v-card-text class="pa-0 lista-rolante">
                                <v-simple-table dense v-if="anuaisAno.length">
                                    <thead>
                                        <tr>
                                            <th>Empresa</th>
                                            <th>Vencimento</th>
                                            <th class="text-right">Valor</th>
                                            <th class="text-right">Equiv. mensal</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="p in anuaisAno" :key="p.empresa_id">
                                            <td>{{ p.fantasia || p.razao_social }}</td>
                                            <td>{{ formatDate(p.vencimento) }}</td>
                                            <td class="text-right">{{ formatCurrency(p.valor) }}</td>
                                            <td class="text-right grey--text">{{ formatCurrency(p.valor_mensal) }}</td>
                                            <td>
                                                <v-chip x-small :color="p.pago ? 'success' : 'primary'" dark>
                                                    {{ p.pago ? 'Pago em ' + formatDate(p.pago_em) : 'A receber' }}
                                                </v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                                <div v-else class="text-center grey--text pa-6">
                                    Nenhum plano anual no ano
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import BarChart from '@/components/charts/BarChart';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export default {
    name: 'PrevisaoFaturamento',

    components: {
        BaseContainer,
        BarChart,
    },

    data: () => ({
        dados: [],
        mesReferencia: '',
        anoReferencia: '',
        clientesAtivos: 0,
        qtdAssinaturasAtivas: 0,
        previstoTotal: 0,
        recebidoTotal: 0,
        aReceberTotal: 0,
        atrasoTotal: 0,
        qtdRecebido: 0,
        qtdAReceber: 0,
        qtdAtraso: 0,
        emAtraso: [],
        anuaisMes: [],
        anuaisAno: [],
        totalAnuaisMes: 0,
        totalAnuaisAno: 0,
        mostrarAtraso: true,
    }),

    computed: {
        percentRecebido() {
            if (!this.previstoTotal) return 0;
            return Math.round((this.recebidoTotal / this.previstoTotal) * 100);
        },

        anuaisPagos() {
            return this.anuaisAno.filter(p => p.pago).length;
        },

        anuaisAReceber() {
            return this.anuaisAno.filter(p => !p.pago).length;
        },

        alturaGrafico() {
            return this.$vuetify.breakpoint.mdAndUp ? 380 : 280;
        },
    },

    mounted() {
        this.consultarAtraso();
        this.consultarPrevisao();
        this.consultarPlanosAnuais();
    },

    methods: {
        consultarPrevisao() {
            this.$http.get('financeiro-interno/previsao-faturamento/semanal')
                .then(resp => {
                    const d = resp.data;
                    this.dados = d.data;
                    this.mesReferencia = d.mes_referencia;
                    this.clientesAtivos = d.clientes_ativos || 0;
                    this.qtdAssinaturasAtivas = d.qtd_assinaturas_ativas || 0;
                    this.previstoTotal = d.previsto_total || 0;
                    this.recebidoTotal = d.recebido_total || 0;
                    this.aReceberTotal = d.a_receber_total || 0;
                    this.atrasoTotal = d.atraso_total || 0;
                    this.qtdRecebido = d.qtd_recebido || 0;
                    this.qtdAReceber = d.qtd_a_receber || 0;
                    this.qtdAtraso = d.qtd_atraso || 0;
                }).catch(() => {
                    this.$root.$emit('notify', { text: 'Não foi possível carregar a previsão', type: 'error' });
                });
        },

        consultarAtraso() {
            this.$http.get('financeiro-interno/previsao-faturamento/inadimplentes')
                .then(resp => {
                    this.emAtraso = resp.data.data;
                }).catch(() => { this.emAtraso = []; });
        },

        consultarPlanosAnuais() {
            this.$http.get('financeiro-interno/previsao-faturamento/planos-anuais')
                .then(resp => {
                    const d = resp.data;
                    this.anuaisMes = d.mes || [];
                    this.anuaisAno = d.ano || [];
                    this.totalAnuaisMes = d.total_mes || 0;
                    this.totalAnuaisAno = d.total_ano || 0;
                    this.anoReferencia = d.ano_referencia || '';
                }).catch(() => {
                    this.anuaisMes = [];
                    this.anuaisAno = [];
                });
        },

        formatCurrency(v) {
            return brl.format(Number(v || 0));
        },

        formatDate(v) {
            if (!v) return '—';
            const [y, m, d] = String(v).slice(0, 10).split('-');
            return `${d}/${m}/${y}`;
        },
    },
};
</script>

<style scoped>
.tela {
    min-height: 100vh;
}

.lista-rolante {
    max-height: 420px;
    overflow-y: auto;
}
</style>
